import { useEffect, useState } from 'react';
import type { MakeModelValue } from '../../../../features/MakeModelKeywordSearch/MakeModelKeywordSearch.typed';
import { searchApi } from '../../../../api/searchApi';
import { logger } from '@dist-marketplace-frontend/services';
import { PAGE } from '../../../../helpers/pages';
import { constructUrlParams } from '../../../../helpers/formatting';
import { pushToDataLayer } from '../../../../services/gtmService';
import {
  formatSearchTerms,
  formatSearchTermsPrice,
  formatSearchTermsYears,
} from '../../../../helpers/formatSearchTerms';
import { restoreScrollPosition } from '../../../../helpers/scroll';
import { useRouter } from 'next/router';
import { useLastSearch } from '../../../../components/PageWrappers/SharedComponents/LastSearchCard/hooks/useLastSearch';

type SelectedFilters = {
  year: {
    min: string | null;
    max: string | null;
  };
  price: {
    min: string | null;
    max: string | null;
  };
};

interface SelectedValue extends MakeModelValue {
  label: string;
}

export const useMotorsSearch = ({
  initialAdCount,
  domain,
}: {
  initialAdCount?: number;
  domain: string;
}) => {
  const router = useRouter();

  const { setLastSearch } = useLastSearch();

  const [keywordSearchValue, setKeywordSearchValue] = useState<string>('');
  const [selectedMake, setSelectedMake] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [adsCount, setAdsCount] = useState<number>(initialAdCount ?? 0);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    year: {
      min: null,
      max: null,
    },
    price: {
      min: null,
      max: null,
    },
  });

  const onSelectOption = (value: SelectedValue | null) => {
    setKeywordSearchValue(value?.label ?? '');
    setSelectedMake(value?.make ?? null);
    setSelectedModel(value?.model ?? null);
  };

  useEffect(() => {
    let isMounted = true;

    // Avoid making API call if the form is pristine
    if (isFormPristine()) {
      setAdsCount(initialAdCount ?? 0);
      return;
    }

    getResultsCount()
      .then((count) => {
        if (isMounted) {
          setAdsCount(count);
        }
      })
      .catch((error) => {
        logger.error({
          tags: [PAGE.HOME],
          error,
          message: 'Error getting results count',
        });
      });

    return () => {
      isMounted = false;
    };

    async function getResultsCount() {
      return await searchApi.getCarsCount({
        make: selectedMake ?? undefined,
        model: selectedModel ?? undefined,
        priceFrom: selectedFilters.price.min ?? undefined,
        priceTo: selectedFilters.price.max ?? undefined,
        yearFrom: selectedFilters.year.min ?? undefined,
        yearTo: selectedFilters.year.max ?? undefined,
      });
    }

    function isFormPristine() {
      return (
        !selectedMake &&
        !selectedModel &&
        !selectedFilters.year.min &&
        !selectedFilters.year.max &&
        !selectedFilters.price.min &&
        !selectedFilters.price.max
      );
    }
  }, [selectedFilters, selectedMake, selectedModel, initialAdCount]);

  const handleSearch = () => {
    const searchPageUrl = buildSearchPageUrl();

    pushToDataLayer({
      event: 'homepage_filter',
      hasMakeModel: Boolean(selectedMake),
      hasPrice:
        selectedFilters.price.min !== null ||
        selectedFilters.price.max !== null,
      hasYear:
        selectedFilters.year.min !== null || selectedFilters.year.max !== null,
      url: searchPageUrl.toString(),
    });

    updateLastSearch(searchPageUrl);
    restoreScrollPosition(searchPageUrl.pathname + searchPageUrl.search);

    router.push(searchPageUrl.toString());

    function buildSearchPageUrl() {
      const motorParams = constructUrlParams({
        price_from: selectedFilters.price.min ?? undefined,
        price_to: selectedFilters.price.max ?? undefined,
        year_from: selectedFilters.year.min ?? undefined,
        year_to: selectedFilters.year.max ?? undefined,
        make: selectedMake ?? undefined,
      });

      let query = motorParams.toString();
      if (selectedModel) {
        query += `;model:${encodeURIComponent(selectedModel)}`;
      }

      const baseMotorUrl = `${domain}/cars`;
      return new URL(query ? `${baseMotorUrl}?${query}` : baseMotorUrl);
    }

    function updateLastSearch(url: URL) {
      const year = formatSearchTermsYears(
        selectedFilters.year.min ?? undefined,
        selectedFilters.year.max ?? undefined,
      );
      const price = formatSearchTermsPrice(
        selectedFilters.price.min ?? undefined,
        selectedFilters.price.max ?? undefined,
        '€',
      );
      const makeAndModel = selectedModel
        ? `${selectedMake} ${selectedModel}`
        : selectedMake;
      const lastSearchTerms = ['Cars', makeAndModel, year, price].filter(
        Boolean,
      );
      setLastSearch(
        formatSearchTerms(lastSearchTerms as string[]),
        url.toString(),
      );
    }
  };

  return {
    onSelectOption,
    selectedFilters,
    selectedMake,
    selectedModel,
    setSelectedFilters,
    adsCount,
    handleSearch,
    keywordSearchValue,
  };
};
